import React, { useState } from 'react';
import { Search, Filter, Download, Eye, FileText, Calendar, Printer } from 'lucide-react';

interface Payslip {
  id: string;
  employeeName: string;
  employeeId: string;
  month: string;
  grossSalary: number;
  netSalary: number;
  generatedAt: string;
  downloadedAt?: string;
}

const mockPayslips: Payslip[] = [
  {
    id: '1',
    employeeName: 'Ahmed Mansouri',
    employeeId: 'EMP-001',
    month: '2024-11',
    grossSalary: 25000,
    netSalary: 20380,
    generatedAt: '2024-11-28',
    downloadedAt: '2024-11-29',
  },
  {
    id: '2',
    employeeName: 'Fatima Bennani',
    employeeId: 'EMP-002',
    month: '2024-11',
    grossSalary: 18000,
    netSalary: 15394,
    generatedAt: '2024-11-28',
  },
  {
    id: '3',
    employeeName: 'Mohammed Tazi',
    employeeId: 'EMP-003',
    month: '2024-11',
    grossSalary: 15000,
    netSalary: 12828,
    generatedAt: '2024-11-28',
    downloadedAt: '2024-11-29',
  },
  {
    id: '4',
    employeeName: 'Ahmed Mansouri',
    employeeId: 'EMP-001',
    month: '2024-10',
    grossSalary: 25000,
    netSalary: 20380,
    generatedAt: '2024-10-28',
    downloadedAt: '2024-10-30',
  },
  {
    id: '5',
    employeeName: 'Fatima Bennani',
    employeeId: 'EMP-002',
    month: '2024-10',
    grossSalary: 18000,
    netSalary: 15394,
    generatedAt: '2024-10-28',
    downloadedAt: '2024-10-29',
  },
];

export const PayslipsPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [monthFilter, setMonthFilter] = useState<string>('2024-11');
  const [payslips] = useState<Payslip[]>(mockPayslips);

  const filteredPayslips = payslips.filter((payslip) => {
    const matchesSearch =
      payslip.employeeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payslip.employeeId.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesMonth = payslip.month === monthFilter;
    return matchesSearch && matchesMonth;
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-MA', {
      style: 'currency',
      currency: 'MAD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatMonth = (monthStr: string) => {
    const [year, month] = monthStr.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  };

  // Stats for current month
  const currentMonthPayslips = payslips.filter((p) => p.month === monthFilter);
  const totalGross = currentMonthPayslips.reduce((sum, p) => sum + p.grossSalary, 0);
  const totalNet = currentMonthPayslips.reduce((sum, p) => sum + p.netSalary, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Bulletins de Paie</h1>
          <p className="text-gray-500 mt-1">
            Historique et téléchargement des bulletins de paie
          </p>
        </div>
        <div className="flex gap-2">
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            <Download size={20} />
            Télécharger Tous
          </button>
          <button className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
            <FileText size={20} />
            Générer Bulletins
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <FileText className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Bulletins {formatMonth(monthFilter)}</p>
              <p className="text-xl font-bold text-gray-900">{currentMonthPayslips.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <Calendar className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Brut</p>
              <p className="text-xl font-bold text-amber-600">{formatCurrency(totalGross)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
              <Calendar className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Net</p>
              <p className="text-xl font-bold text-green-600">{formatCurrency(totalNet)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Rechercher par nom, ID employé..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <Calendar size={20} className="text-gray-400" />
            <select
              value={monthFilter}
              onChange={(e) => setMonthFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="2024-11">Novembre 2024</option>
              <option value="2024-10">Octobre 2024</option>
              <option value="2024-09">Septembre 2024</option>
            </select>
          </div>
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
            <Filter size={20} />
            Filtres
          </button>
        </div>
      </div>

      {/* Payslips Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Employé
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Période
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Brut
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Net
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Généré le
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredPayslips.map((payslip) => (
                <tr key={payslip.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-8 w-8 rounded-lg bg-primary-100 flex items-center justify-center mr-3">
                        <FileText className="h-4 w-4 text-primary-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{payslip.employeeName}</p>
                        <p className="text-sm text-gray-500">{payslip.employeeId}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center text-gray-600">
                    {formatMonth(payslip.month)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right font-medium text-gray-900">
                    {formatCurrency(payslip.grossSalary)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right font-bold text-green-600">
                    {formatCurrency(payslip.netSalary)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center text-gray-600">
                    {formatDate(payslip.generatedAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    {payslip.downloadedAt ? (
                      <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">
                        Téléchargé
                      </span>
                    ) : (
                      <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700">
                        Non téléchargé
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        className="p-1.5 text-gray-400 hover:text-primary-600 transition-colors"
                        title="Voir"
                      >
                        <Eye size={18} />
                      </button>
                      <button
                        className="p-1.5 text-gray-400 hover:text-primary-600 transition-colors"
                        title="Télécharger PDF"
                      >
                        <Download size={18} />
                      </button>
                      <button
                        className="p-1.5 text-gray-400 hover:text-primary-600 transition-colors"
                        title="Imprimer"
                      >
                        <Printer size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredPayslips.length === 0 && (
          <div className="text-center py-12">
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun bulletin trouvé</h3>
            <p className="mt-1 text-sm text-gray-500">
              Aucun bulletin de paie pour cette période.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
